/**
 * recruiterReportUtils.js
 * Generates standalone, print-ready, beautifully styled HTML reports
 * for Recruiter Performance Report with full filter reflection.
 */

export function triggerHtmlDownload(htmlContent, fileName) {
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const commonCss = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    background: #f8fafc;
    color: #1e293b;
    padding: 24px;
    line-height: 1.5;
  }
  .report-container {
    max-width: 1100px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 12px;
    padding: 24px 28px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 2px solid #3b5bfd;
    padding-bottom: 16px;
    margin-bottom: 18px;
  }
  .brand-title {
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
  }
  .brand-title span {
    color: #3b5bfd;
  }
  .meta-info {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
  }
  .download-pdf-btn, .print-btn {
    background: #3b5bfd;
    color: #ffffff;
    border: none;
    padding: 7px 16px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .download-pdf-btn:hover, .print-btn:hover {
    background: #134e48;
  }
  .filters-banner {
    background: #f1f5f9;
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .filter-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #475569;
  }
  .badge {
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .badge.date { background: #e0f2fe; color: #0284c7; }
  .badge.client { background: #fef3c7; color: #d97706; }
  .badge.status { background: #f3e8ff; color: #7c3aed; }
  
  .kpi-grid-7 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 10px;
    margin-bottom: 20px;
  }
  .kpi-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 10px;
    text-align: center;
    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  }
  .kpi-card.blue { border-top: 3.5px solid #3b82f6; }
  .kpi-card.amber { border-top: 3.5px solid #f59e0b; }
  .kpi-card.purple { border-top: 3.5px solid #8b5cf6; }
  .kpi-card.sky { border-top: 3.5px solid #0284c7; }
  .kpi-card.teal { border-top: 3.5px solid #0d9488; }
  .kpi-card.green { border-top: 3.5px solid #10b981; }
  .kpi-card.red { border-top: 3.5px solid #ef4444; }
  
  .kpi-title { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #64748b; margin-bottom: 4px; }
  .kpi-value { font-size: 22px; font-weight: 800; line-height: 1.1; margin-bottom: 2px; }
  .kpi-sub { font-size: 9.5px; color: #94a3b8; }
  
  table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11.5px; }
  th { background: #f8fafc; color: #475569; font-weight: 700; text-transform: uppercase; font-size: 10px; padding: 8px 6px; text-align: center; border: 1px solid #e2e8f0; }
  th:nth-child(2) { text-align: left; }
  td { border: 1px solid #e2e8f0; }
  tbody tr:nth-child(even) { background: #fafafa; }
  tbody tr:hover { background: #f0fdfa; }

  @media print {
    body { background: #fff; padding: 0; }
    .report-container { box-shadow: none; border: none; padding: 0; }
    .download-pdf-btn, .print-btn { display: none; }
  }
`;

const pdfScriptTag = `
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
  <script>
    function downloadAsPDF() {
      const element = document.querySelector('.report-container');
      const opt = {
        margin: [10, 10, 10, 10],
        filename: 'Recruiter_Performance_Report.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
      };
      html2pdf().set(opt).from(element).save();
    }
  </script>
`;

export function generateRecruiterPerformanceReportHtml({
  startDate,
  endDate,
  selectedRecruiter = '',
  timeframe = 'All Time',
  region = 'Offshore',
  summaryTotals = {},
  reportData = []
}) {
  const isOffshore = String(region).toLowerCase() === 'offshore';
  const regionLabel = isOffshore ? 'Offshore' : 'Onsite';
  const dateRange = (startDate && endDate) ? `${startDate} to ${endDate}` : (timeframe || 'All Time');
  const recruiterLabel = selectedRecruiter || `All ${regionLabel} Recruiters`;
  const now = new Date().toLocaleString();

  const recruiterRowsHtml = (reportData || []).map((row, idx) => `
    <tr>
      <td style="color: #64748b; font-size: 10px; text-align: center; padding: 8px 6px;">${idx + 1}</td>
      <td style="font-weight: 700; color: #3b5bfd; padding: 8px 10px;">${row.recruiterName || '-'}</td>
      <td style="text-align: center; font-weight: 800; color: #0f172a; padding: 8px 6px;">${row.totalProfiles || 0}</td>
      <td style="text-align: center; font-weight: 600; color: #d97706; padding: 8px 6px;">${row.screenedCurrent || 0}</td>
      <td style="text-align: center; font-weight: 600; color: #7c3aed; padding: 8px 6px;">${row.clientSubmitCurrent || 0}</td>
      <td style="text-align: center; font-weight: 600; color: #0284c7; padding: 8px 6px;">${row.l1Current || 0}</td>
      <td style="text-align: center; font-weight: 600; color: #0f766e; padding: 8px 6px;">${row.l2Current || 0}</td>
      <td style="text-align: center; font-weight: 700; color: #10b981; padding: 8px 6px;">${row.closureCurrent || 0}</td>
      <td style="text-align: center; font-weight: 600; color: #ef4444; padding: 8px 6px;">${row.rejectedCurrent || 0}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Prophecy ERP — ${regionLabel} Recruiter Performance Report (${dateRange})</title>
  <style>
    ${commonCss}
  </style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div>
        <div class="brand-title">PROPHECY <span>ERP</span> &mdash; ${regionLabel} Recruiter Performance Report</div>
        <div class="meta-info">Current Stage Snapshot of ${regionLabel} Recruiter Pipeline &bull; Generated on: ${now}</div>
      </div>
      <button class="download-pdf-btn" id="downloadPdfBtn" onclick="downloadAsPDF()">📥 Download as PDF</button>
    </div>

    <div class="filters-banner">
      <span class="filter-label">Active Filters:</span>
      <span class="badge" style="background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0;">🌍 Region: ${regionLabel}</span>
      <span class="badge date">📅 Date Range: ${dateRange}</span>
      <span class="badge client">👤 Recruiter: ${recruiterLabel}</span>
      <span class="badge status">⏱️ Timeframe: ${timeframe}</span>
    </div>

    <div class="kpi-grid-7">
      <div class="kpi-card blue">
        <div class="kpi-title">Total Profiles</div>
        <div class="kpi-value" style="color: #3b82f6;">${summaryTotals.totalProfiles || 0}</div>
        <div class="kpi-sub">All submissions</div>
      </div>
      <div class="kpi-card amber">
        <div class="kpi-title">Screened</div>
        <div class="kpi-value" style="color: #f59e0b;">${summaryTotals.screenedCurrent || 0}</div>
        <div class="kpi-sub">In screening</div>
      </div>
      <div class="kpi-card purple">
        <div class="kpi-title">Client Submit</div>
        <div class="kpi-value" style="color: #8b5cf6;">${summaryTotals.clientSubmitCurrent || 0}</div>
        <div class="kpi-sub">Client review</div>
      </div>
      <div class="kpi-card sky">
        <div class="kpi-title">L1 Interview</div>
        <div class="kpi-value" style="color: #0284c7;">${summaryTotals.l1Current || 0}</div>
        <div class="kpi-sub">Round 1</div>
      </div>
      <div class="kpi-card teal">
        <div class="kpi-title">L2 Interview</div>
        <div class="kpi-value" style="color: #0d9488;">${summaryTotals.l2Current || 0}</div>
        <div class="kpi-sub">Round 2/Final</div>
      </div>
      <div class="kpi-card green">
        <div class="kpi-title">Closure / Hired</div>
        <div class="kpi-value" style="color: #10b981;">${summaryTotals.closureCurrent || 0}</div>
        <div class="kpi-sub">Offer / Placed</div>
      </div>
      <div class="kpi-card red">
        <div class="kpi-title">Rejected / Withdrawn</div>
        <div class="kpi-value" style="color: #ef4444;">${summaryTotals.rejectedCurrent || 0}</div>
        <div class="kpi-sub">Internal / Client</div>
      </div>
    </div>

    <div>
      <h2 style="font-size: 14px; font-weight: 800; color: #0f172a; margin-bottom: 6px;">
        Recruiter Performance Breakdown (${(reportData || []).length} Recruiters)
      </h2>
      <table>
        <thead>
          <tr>
            <th style="width: 35px">#</th>
            <th style="text-align: left">Recruiter</th>
            <th>Total Profiles</th>
            <th>Screened</th>
            <th>Client Submit</th>
            <th>L1 Interview</th>
            <th>L2 Interview</th>
            <th>Closure</th>
            <th>Rejected / Withdrawn</th>
          </tr>
        </thead>
        <tbody>
          ${recruiterRowsHtml || '<tr><td colspan="9" style="padding: 16px; text-align: center; color: #94a3b8">No recruiter performance data available</td></tr>'}
        </tbody>
      </table>
    </div>

    <div class="footer" style="margin-top: 20px; padding-top: 14px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8;">
      <span>Prophecy ERP &bull; Offshore Recruiter Analytics &bull; Confidential</span>
      <span>Page 1 of 1</span>
    </div>
  </div>
  ${pdfScriptTag}
</body>
</html>`;

  const safeStart = startDate || 'All';
  const safeEnd = endDate || 'Time';
  const fileName = `Recruiter_Performance_Report_${safeStart}_to_${safeEnd}.html`;
  triggerHtmlDownload(html, fileName);
}
